// src/pdf-calcs/atm-calculations.js
// ATM calculation logic. No DOM or jQuery dependencies.

// Semi Trailer: minimum required trailer ATM.
// Source: assessment-tool-atm-calculator-semi-trailer.pdf

export function calculateAtmSemiTrailer({ steerAxleMass, driveAxleGroupMass, trailerAxleGroupMass, primeMoverTare }) {
   const minimumRequiredTrailerAtm = steerAxleMass + driveAxleGroupMass + trailerAxleGroupMass - primeMoverTare;

   return {
      minimumRequiredTrailerAtm,
   };
}

// Dog Trailer: minimum required ATM for dolly, trailer and combined dog trailer unit.
//
// Vehicle layout:
//   Dolly (own axle group) → Dog Trailer (own axle group)
//
// Formulas:
//   Dolly ATM       = dollyAxleGroupMass
//   Trailer ATM     = dollyAxleGroupMass + trailerAxleGroupMass − dollyTareMass
//   Dog Trailer ATM = dollyAxleGroupMass + trailerAxleGroupMass
//
// Returns ok: false if any input is non-finite or if trailerATM < 0 (physically invalid).

export function calculateAtmDogTrailer({ dollyAxleGroupMass, trailerAxleGroupMass, dollyTareMass }) {
   if (
      !Number.isFinite(dollyAxleGroupMass) ||
      !Number.isFinite(trailerAxleGroupMass) ||
      !Number.isFinite(dollyTareMass)
   ) {
      return { ok: false, dollyATM: NaN, trailerATM: NaN, dogTrailerATM: NaN };
   }

   const dollyATM = dollyAxleGroupMass;
   const trailerATM = dollyAxleGroupMass + trailerAxleGroupMass - dollyTareMass;

   if (trailerATM < 0) {
      return { ok: false, dollyATM, trailerATM, dogTrailerATM: NaN };
   }

   const dogTrailerATM = dollyAxleGroupMass + trailerAxleGroupMass;

   return { ok: true, dollyATM, trailerATM, dogTrailerATM };
}

// B-Double Combination: minimum required ATM for A-trailer and B-trailer.
// Source: assessment-tool-atm-calculator-b-double.pdf
//
// Vehicle layout (left to right):
//   PM (steer + drive) → 5th wheel → A-Trailer → B-Trailer
//
// Formulas match the PDF embedded JavaScript exactly:
//   MRATMA = (C19 + E19 + I19) - E21
//   MRATMB = (C19 + E19 + I19 + M19) - (E21 + I21 + I24)

export function calculateAtmBDouble({
   steerAxleMass, // C19
   driveAxleGroupMass, // E19
   aTrailerAxleGroupMass, // I19
   bTrailerAxleGroupMass, // M19
   pmTare, // E21
   aTrailerTare, // I21
   aTrailerPayload, // I24
}) {
   const minimumRequiredATrailerAtm = steerAxleMass + driveAxleGroupMass + aTrailerAxleGroupMass - pmTare;
   const minimumRequiredBTrailerAtm =
      steerAxleMass +
      driveAxleGroupMass +
      aTrailerAxleGroupMass +
      bTrailerAxleGroupMass -
      pmTare -
      aTrailerTare -
      aTrailerPayload;

   return {
      minimumRequiredATrailerAtm,
      minimumRequiredBTrailerAtm,
   };
}

// Low Loader Combination: minimum required ATM for dolly and trailer.
// Source: assessment-tool-atm-calculator-low-loader-combination.pdf
//
// Vehicle layout (left to right):
//   PM (steer + drive) → Dolly → Low Loader Trailer
//
// Formulas match the PDF embedded JavaScript exactly:
//   DATM = (D21 + F21 + I21) - F23
//   TATM = (D21 + F21 + I21 + L21) - (F23 + I23)

export function calculateAtmLowLoader({
   steerAxleMass, // D21
   driveAxleGroupMass, // F21
   dollyAxleGroupMass, // I21
   trailerAxleGroupMass, // L21
   pmTare, // F23
   dollyTare, // I23
}) {
   const minimumRequiredDollyAtm = steerAxleMass + driveAxleGroupMass + dollyAxleGroupMass - pmTare;
   const minimumRequiredTrailerAtm =
      steerAxleMass + driveAxleGroupMass + dollyAxleGroupMass + trailerAxleGroupMass - pmTare - dollyTare;

   return {
      minimumRequiredDollyAtm,
      minimumRequiredTrailerAtm,
   };
}

// Road Train Combination: minimum required ATM for each trailer and dolly.
// Source: assessment-tool-atm-calculator-road-train-combination.pdf
//
// Vehicle layout (left to right):
//   PM (steer + drive) → 5th wheel → Trailer 1 → Dolly 1 → Trailer 2 → Dolly 2 → Trailer 3
//
// Formulas match the PDF embedded JavaScript exactly:
//   TATM  = (D21 + F21 + J21) - F23   → Min Required 1st Trailer ATM
//   DATM  = M21                         → Min Required 1st Dolly ATM
//   TATM2 = (M21 + Q21) - M23          → Min Required 2nd Trailer ATM
//   DATM2 = U21                         → Min Required 2nd Dolly ATM
//   TATM3 = (U21 + X21) - U23          → Min Required 3rd Trailer ATM

export function calculateAtmRoadTrain({
   steerAxleMass, // D21
   driveAxleGroupMass, // F21
   trailer1AxleGroupMass, // J21
   dolly1AxleGroupMass, // M21
   trailer2AxleGroupMass, // Q21
   dolly2AxleGroupMass, // U21
   trailer3AxleGroupMass, // X21
   pmTare, // F23
   trailer1Tare, // M23
   trailer2Tare, // U23
}) {
   const minimumRequired1stTrailerAtm = steerAxleMass + driveAxleGroupMass + trailer1AxleGroupMass - pmTare;
   const minimumRequired1stDollyAtm = dolly1AxleGroupMass;
   const minimumRequired2ndTrailerAtm = dolly1AxleGroupMass + trailer2AxleGroupMass - trailer1Tare;
   const minimumRequired2ndDollyAtm = dolly2AxleGroupMass;
   const minimumRequired3rdTrailerAtm = dolly2AxleGroupMass + trailer3AxleGroupMass - trailer2Tare;

   return {
      minimumRequired1stTrailerAtm,
      minimumRequired1stDollyAtm,
      minimumRequired2ndTrailerAtm,
      minimumRequired2ndDollyAtm,
      minimumRequired3rdTrailerAtm,
   };
}
